import React, { AllHTMLAttributes, ButtonHTMLAttributes, FC, PropsWithChildren } from 'react';

import { ChevronIcon, CrossIcon } from 'components/common/icons';
import { cn } from 'utils';

import styles from './FilterBar.module.scss';

type FilterBarProps = AllHTMLAttributes<HTMLDivElement>;

type PillProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  active?: boolean;
  showClear?: boolean;
  onClear?: () => void;
};

type ToggleProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  expanded?: boolean;
  label?: string;
};

const FilterBarRoot: FC<PropsWithChildren<FilterBarProps>> = ({
  children,
  className,
  ...props
}) => (
  <div className={cn(styles.bar, className)} {...props}>
    {children}
  </div>
);

const Pills: FC<PropsWithChildren> = ({ children }) => (
  <div className={styles.pills}>{children}</div>
);

const Pill: FC<PropsWithChildren<PillProps>> = ({
  children,
  active = false,
  showClear = false,
  onClear,
  className,
  onClick,
  ...props
}) => {
  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    onClear?.();
  };

  return (
    <button
      type="button"
      className={cn(styles.pill, active && styles.pillActive, className)}
      onClick={onClick}
      {...props}
    >
      <span className={styles.pillLabel}>{children}</span>
      {active && showClear && (
        <span className={styles.pillClear} onClick={handleClear} role="presentation">
          <CrossIcon size={18} preset="circle-white" />
        </span>
      )}
    </button>
  );
};

const Toggle: FC<ToggleProps> = ({
  expanded = false,
  label = 'Показать фильтры',
  className,
  ...props
}) => (
  <button type="button" className={cn(styles.toggle, className)} {...props}>
    <span>{label}</span>
    <ChevronIcon direction={expanded ? 'down' : 'up'} size={20} color="var(--black-color)" />
  </button>
);

const Actions: FC<PropsWithChildren> = ({ children }) => (
  <div className={styles.actions}>{children}</div>
);

const Content: FC<PropsWithChildren<{ visible?: boolean }>> = ({ children, visible = false }) => {
  if (!visible) {
    return null;
  }

  return <div className={styles.content}>{children}</div>;
};

type FilterBarExtensions = {
  Pills: typeof Pills;
  Pill: typeof Pill;
  Actions: typeof Actions;
  Toggle: typeof Toggle;
  Content: typeof Content;
};

const FilterBar = FilterBarRoot as typeof FilterBarRoot & FilterBarExtensions;

FilterBar.Pills = Pills;
FilterBar.Pill = Pill;
FilterBar.Actions = Actions;
FilterBar.Toggle = Toggle;
FilterBar.Content = Content;

export default FilterBar;
